// ============================================
// MoltiVerse v3 - Global State Store
// ============================================

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { Agent, Connection, GraphData, UniverseStats, TimeSlice } from '@/lib/types';
import { universeLoader } from '@/lib/data-engine';

// ============ TYPES ============
interface ViewState {
  mode: 'galaxy' | 'pathfinder' | 'timelapse';
  focusedAgentId: string | null;
  highlightedConnections: Set<string>;
  visibleSubmolts: Set<string>;
  minKarmaFilter: number;
  minConnectionFilter: number;
  show3rdLayer: boolean;
}

interface TimeState {
  currentTimestamp: number;
  isPlaying: boolean;
  playSpeed: number; // ms per tick
  timeSlices: TimeSlice[];
  minTime: number;
  maxTime: number;
}

interface SearchHistoryItem {
  id: string;
  name: string;
  main_submolt: string;
  karma: number;
  timestamp: number;
}

interface PostSearchResult {
  id: string;
  author_id: string;
  author_name: string;
  author_karma: number;
  author_submolt: string;
  submolt: string;
  title: string | null;
  content: string;
  upvotes: number;
  downvotes: number;
  created_at: string;
}

interface SearchState {
  isOpen: boolean;
  query: string;
  mode: 'agents' | 'posts';
  results: Agent[];
  postResults: PostSearchResult[];
  isSearchingPosts: boolean;
  history: SearchHistoryItem[];
}

interface SettingsState {
  showAllAgents: boolean; // Experimental: load all agents (may lag)
}

interface NavigationHistoryItem {
  type: 'agent' | 'galaxy';
  agentId?: string;
  timestamp: number;
}

interface UniverseState {
  // Data
  graphData: GraphData;
  stats: UniverseStats;
  isLoading: boolean;
  loadProgress: number;
  
  // Settings
  settings: SettingsState;
  error: string | null;

  // View state
  view: ViewState;
  
  // Time machine
  time: TimeState;
  
  // Search
  search: SearchState;
  
  // Selected agent details
  selectedAgent: Agent | null;
  agentInfluencers: Array<Agent & { _interactionWeight: number; _interactionCount: number }>;
  agentInfluenced: Array<Agent & { _interactionWeight: number; _interactionCount: number }>;
  agentConnections: { direct: Connection[]; secondDegree: Connection[] };
  
  // Navigation history
  navigationHistory: NavigationHistoryItem[];
  canGoBack: boolean;

  // Actions
  initialize: () => Promise<void>;
  loadUniverse: (showAll?: boolean, forceReload?: boolean) => Promise<void>;
  toggleShowAllAgents: () => void;
  
  // View actions
  setViewMode: (mode: ViewState['mode']) => void;
  focusAgent: (agentId: string | null, addToHistory?: boolean) => void | Promise<void>;
  goBack: () => void;
  setSubmoltFilter: (submolts: Set<string>) => void;
  setKarmaFilter: (min: number) => void;
  setConnectionFilter: (min: number) => void;
  toggle3rdLayer: () => void;
  
  // Time actions
  setTimestamp: (timestamp: number) => void;
  playTimelapse: () => void;
  pauseTimelapse: () => void;
  setPlaySpeed: (speed: number) => void;
  
  // Search actions
  openSearch: () => void;
  closeSearch: () => void;
  setSearchQuery: (query: string) => void;
  setSearchMode: (mode: 'agents' | 'posts') => void;
  searchPosts: (query: string) => Promise<void>;
  addToSearchHistory: (agent: Agent) => void;
  
  // Agent actions
  selectAgent: (agent: Agent | null) => void;
  getAgentById: (id: string) => Agent | undefined;
}

// ============ STORE ============
export const useUniverseStore = create<UniverseState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    graphData: { nodes: [], links: [] },
    stats: {
      totalAgents: 0,
      totalPosts: 0,
      totalInteractions: 0,
      loadedAgents: 0,
      loadedConnections: 0,
      cacheSize: 0,
      isStreaming: false,
      streamProgress: 0,
      oldestTimestamp: Date.now(),
      newestTimestamp: Date.now(),
    },
    isLoading: false,
    loadProgress: 0,
    error: null,
    
    settings: {
      showAllAgents: localStorage.getItem('moltiverse-show-all-agents') === 'true',
    },

    view: {
      mode: 'galaxy',
      focusedAgentId: null,
      highlightedConnections: new Set(),
      visibleSubmolts: new Set(['crustafarian', 'bug-hunters', 'insurgency', 'philosophy', 'marketplace', 'memes', 'default']),
      minKarmaFilter: 0,
      minConnectionFilter: 1,
      show3rdLayer: false,
    },

    time: {
      currentTimestamp: Date.now(),
      isPlaying: false,
      playSpeed: 100, // 100ms per time step
      timeSlices: [],
      minTime: 0,
      maxTime: Date.now(),
    },

    search: {
      isOpen: false,
      query: '',
      mode: 'agents' as const,
      results: [],
      postResults: [],
      isSearchingPosts: false,
      history: JSON.parse(localStorage.getItem('moltiverse-search-history') || '[]'),
    },
    
    // Navigation history for back button support
    navigationHistory: [],
    canGoBack: false,

    selectedAgent: null,
    agentInfluencers: [],
    agentInfluenced: [],
    agentConnections: { direct: [], secondDegree: [] },

    // ============ INITIALIZATION ============
    
    initialize: async () => {
      console.log('🚀 Initializing MoltiVerse v3...');
      await universeLoader.initialize();
      
      // Check if we have cached data
      const stats = universeLoader.getStats();
      if (stats.loadedAgents > 0) {
        const graphData = universeLoader.getFullGraph();
        const timeSlices = universeLoader.getTimeSlices(100);
        
        set({
          graphData,
          stats,
          time: {
            ...get().time,
            timeSlices,
            minTime: stats.oldestTimestamp,
            maxTime: stats.newestTimestamp,
            currentTimestamp: stats.newestTimestamp,
          },
        });
      }
    },

    loadUniverse: async (showAll?: boolean, forceReload?: boolean) => {
      const useShowAll = showAll ?? get().settings.showAllAgents;
      set({ isLoading: true, loadProgress: 0, error: null });

      try {
        // Only clear data when explicitly forcing a reload (e.g., toggling show all)
        if (forceReload) {
          universeLoader.clear();
        }
        
        // Stream universe but only update progress, not the graph
        // This keeps the loading screen visible until everything is ready
        await universeLoader.streamUniverse(
          (chunk) => {
            // Only update progress for the loading screen
            set({ loadProgress: chunk.progress });
          },
          (error) => {
            set({ error: error.message });
          },
          { showAllAgents: useShowAll }
        );

        // All data loaded - now update everything at once
        const stats = universeLoader.getStats();
        const graphData = universeLoader.getFullGraph();
        const timeSlices = universeLoader.getTimeSlices(100);

        set({
          isLoading: false,
          loadProgress: 100,
          graphData,
          stats,
          time: {
            ...get().time,
            timeSlices,
            minTime: stats.oldestTimestamp,
            maxTime: stats.newestTimestamp,
            currentTimestamp: stats.newestTimestamp,
          },
        });
      } catch (error) {
        set({
          isLoading: false,
          error: (error as Error).message,
        });
      }
    },
    
    toggleShowAllAgents: () => {
      const newValue = !get().settings.showAllAgents;
      localStorage.setItem('moltiverse-show-all-agents', String(newValue));
      set({
        settings: { ...get().settings, showAllAgents: newValue },
      });
      // Reload the universe with new setting (force reload to apply new limit)
      get().loadUniverse(newValue, true);
    },

    // ============ VIEW ACTIONS ============
    
    setViewMode: (mode) => {
      const state = get();
      
      // Quick mode switch - just update mode, don't rebuild graph
      set({
        view: { ...state.view, mode },
      });
      
      // Reset focus when changing modes
      if (mode === 'galaxy') {
        // Only rebuild graph if coming from timelapse (which has filtered data)
        // Pathfinder exit keeps the same graphData, Scene handles position restore
        if (state.view.mode === 'timelapse') {
          const fullGraph = universeLoader.getFullGraph();
          set({
            view: { ...state.view, mode, focusedAgentId: null },
            selectedAgent: null,
            graphData: fullGraph,
          });
        } else {
          // Coming from pathfinder - just clear focus, keep graph
          set({
            view: { ...state.view, mode, focusedAgentId: null },
            selectedAgent: null,
          });
        }
      }
      
      // Start from the BEGINNING when entering Genesis mode
      if (mode === 'timelapse') {
        const { minTime } = state.time;
        const graphAtStart = universeLoader.getGraphAtTimestamp(minTime);
        set({
          time: { ...state.time, currentTimestamp: minTime, isPlaying: false },
          graphData: graphAtStart,
        });
      }
    },

    focusAgent: async (agentId, addToHistory = true) => {
      const state = get();
      const view = state.view;
      
      if (agentId) {
        const agent = universeLoader.getAgent(agentId);
        
        // Add current location to history before navigating (if we're coming from somewhere)
        let newHistory = [...state.navigationHistory];
        if (addToHistory && view.focusedAgentId && view.focusedAgentId !== agentId) {
          // Add the previous agent to history
          newHistory.push({
            type: 'agent',
            agentId: view.focusedAgentId,
            timestamp: Date.now(),
          });
          // Keep only last 50 items
          if (newHistory.length > 50) newHistory = newHistory.slice(-50);
        } else if (addToHistory && view.mode === 'galaxy') {
          // Coming from galaxy view
          newHistory.push({
            type: 'galaxy',
            timestamp: Date.now(),
          });
          if (newHistory.length > 50) newHistory = newHistory.slice(-50);
        }
        
        // Set initial state immediately for responsiveness
        set({
          view: {
            ...view,
            mode: 'pathfinder',
            focusedAgentId: agentId,
          },
          selectedAgent: agent || null,
          agentInfluencers: [],
          agentInfluenced: [],
          agentConnections: { direct: [], secondDegree: [] },
          navigationHistory: newHistory,
          canGoBack: newHistory.length > 0,
        });
        
        // Fetch network data from API for accurate connections
        try {
          const response = await fetch(`/api/agents/${agentId}/network`);
          if (response.ok) {
            const network = await response.json();
            
            // API response type for network data
            interface NetworkAgentResponse extends Agent {
              interaction_weight?: string | number;
              interaction_count?: string | number;
            }
            
            // Transform API response to match expected format
            // Parse as numbers since DB returns strings
            const influencers = (network.influencers || []).map((a: NetworkAgentResponse) => ({
              ...a,
              _interactionWeight: parseFloat(String(a.interaction_weight)) || 0,
              _interactionCount: parseInt(String(a.interaction_count)) || 0,
            }));
            
            const influenced = (network.influenced || []).map((a: NetworkAgentResponse) => ({
              ...a,
              _interactionWeight: parseFloat(String(a.interaction_weight)) || 0,
              _interactionCount: parseInt(String(a.interaction_count)) || 0,
            }));
            
            // Build connections from influencers + influenced
            type EnhancedAgent = NetworkAgentResponse & { _interactionWeight: number; _interactionCount: number };
            const direct = [
              ...influencers.map((a: EnhancedAgent) => ({ 
                source: a.id, 
                target: agentId, 
                weight: a._interactionWeight,
                count: a._interactionCount,
                _degree: 1 
              })),
              ...influenced.map((a: EnhancedAgent) => ({ 
                source: agentId, 
                target: a.id, 
                weight: a._interactionWeight,
                count: a._interactionCount,
                _degree: 1 
              })),
            ];
            
            set({
              agentInfluencers: influencers,
              agentInfluenced: influenced,
              agentConnections: {
                direct,
                secondDegree: [],
              },
            });
          }
        } catch (error) {
          console.warn('Failed to fetch agent network:', error);
          // Fall back to local data
          const connections = universeLoader.getAgentConnections(agentId);
          set({
            agentInfluencers: connections.influencers,
            agentInfluenced: connections.influenced,
            agentConnections: {
              direct: connections.direct,
              secondDegree: connections.secondDegree,
            },
          });
        }
      } else {
        // FAST EXIT: Single atomic update, no graph rebuild
        set({
          view: {
            ...view,
            mode: 'galaxy',
            focusedAgentId: null,
          },
          selectedAgent: null,
          // Clear connection data to free memory
          agentInfluencers: [],
          agentInfluenced: [],
          agentConnections: { direct: [], secondDegree: [] },
        });
      }
    },
    
    goBack: () => {
      const state = get();
      const history = [...state.navigationHistory];
      
      if (history.length === 0) return;
      
      // Pop the last item
      const lastItem = history.pop();
      
      // Update state
      set({
        navigationHistory: history,
        canGoBack: history.length > 0,
      });
      
      // Navigate to the previous location (without adding to history)
      if (lastItem?.type === 'galaxy') {
        get().focusAgent(null, false);
      } else if (lastItem?.type === 'agent' && lastItem.agentId) {
        get().focusAgent(lastItem.agentId, false);
      }
    },

    setSubmoltFilter: (submolts) => {
      set({
        view: { ...get().view, visibleSubmolts: submolts },
      });
    },

    setKarmaFilter: (min) => {
      set({
        view: { ...get().view, minKarmaFilter: min },
      });
    },

    setConnectionFilter: (min) => {
      set({
        view: { ...get().view, minConnectionFilter: min },
      });
    },
    
    toggle3rdLayer: () => {
      set({
        view: { ...get().view, show3rdLayer: !get().view.show3rdLayer },
      });
    },

    // ============ TIME ACTIONS ============
    
    setTimestamp: (timestamp) => {
      const graphData = universeLoader.getGraphAtTimestamp(timestamp);
      
      set({
        time: { ...get().time, currentTimestamp: timestamp },
        graphData,
      });
    },

    playTimelapse: () => {
      set({
        time: { ...get().time, isPlaying: true },
      });

      // Start animation loop
      const animate = () => {
        const { time } = get();
        if (!time.isPlaying) return;

        const sliceIndex = time.timeSlices.findIndex(
          s => s.timestamp >= time.currentTimestamp
        );
        
        if (sliceIndex < time.timeSlices.length - 1) {
          const nextTimestamp = time.timeSlices[sliceIndex + 1].timestamp;
          get().setTimestamp(nextTimestamp);
          setTimeout(animate, time.playSpeed);
        } else {
          // Reached the end
          set({ time: { ...get().time, isPlaying: false } });
        }
      };

      animate();
    },

    pauseTimelapse: () => {
      set({
        time: { ...get().time, isPlaying: false },
      });
    },

    setPlaySpeed: (speed) => {
      set({
        time: { ...get().time, playSpeed: speed },
      });
    },

    // ============ SEARCH ACTIONS ============
    
    openSearch: () => {
      set({ search: { ...get().search, isOpen: true } });
    },

    closeSearch: () => {
      set({
        search: { ...get().search, isOpen: false, query: '', results: [], postResults: [], mode: 'agents' },
      });
    },

    setSearchQuery: (query) => {
      const results = universeLoader.searchAgents(query);
      set({
        search: { ...get().search, query, results },
      });
      
      // If in posts mode, also trigger post search
      if (get().search.mode === 'posts' && query.length >= 2) {
        get().searchPosts(query);
      }
    },
    
    setSearchMode: (mode) => {
      set({
        search: { ...get().search, mode, postResults: [] },
      });
      
      // If switching to posts and we have a query, search posts
      const query = get().search.query;
      if (mode === 'posts' && query.length >= 2) {
        get().searchPosts(query);
      }
    },
    
    searchPosts: async (query) => {
      if (query.length < 2) return;
      
      set({
        search: { ...get().search, isSearchingPosts: true },
      });
      
      try {
        const response = await fetch(`/api/posts/search?q=${encodeURIComponent(query)}&limit=20`);
        if (!response.ok) throw new Error('Search failed');
        
        const data = await response.json();
        set({
          search: { ...get().search, postResults: data.posts || [], isSearchingPosts: false },
        });
      } catch (error) {
        console.error('Post search error:', error);
        set({
          search: { ...get().search, postResults: [], isSearchingPosts: false },
        });
      }
    },
    
    addToSearchHistory: (agent: Agent) => {
      const current = get().search.history;
      // Remove if already exists
      const filtered = current.filter(h => h.id !== agent.id);
      // Add to front, limit to 10
      const newHistory = [
        {
          id: agent.id,
          name: agent.name,
          main_submolt: agent.main_submolt,
          karma: agent.karma || 0,
          timestamp: Date.now(),
        },
        ...filtered,
      ].slice(0, 10);
      
      // Persist to localStorage
      localStorage.setItem('moltiverse-search-history', JSON.stringify(newHistory));
      
      set({
        search: { ...get().search, history: newHistory },
      });
    },

    // ============ AGENT ACTIONS ============
    
    selectAgent: (agent) => {
      if (agent) {
        get().focusAgent(agent.id);
      } else {
        set({ selectedAgent: null });
      }
    },

    getAgentById: (id) => {
      return universeLoader.getAgent(id);
    },
  }))
);

// ============ KEYBOARD SHORTCUTS ============
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', (e) => {
    // Cmd/Ctrl + K to open search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      const { search, openSearch, closeSearch } = useUniverseStore.getState();
      if (search.isOpen) {
        closeSearch();
      } else {
        openSearch();
      }
    }
    
    // Escape to close search or unfocus agent
    if (e.key === 'Escape') {
      const { search, closeSearch, view, focusAgent } = useUniverseStore.getState();
      if (search.isOpen) {
        closeSearch();
      } else if (view.focusedAgentId) {
        focusAgent(null);
      }
    }
    
    // Space to toggle timelapse play
    if (e.key === ' ' && e.target === document.body) {
      e.preventDefault();
      const { time, playTimelapse, pauseTimelapse, view } = useUniverseStore.getState();
      if (view.mode === 'timelapse') {
        if (time.isPlaying) {
          pauseTimelapse();
        } else {
          playTimelapse();
        }
      }
    }
  });
}
